if (!customElements.get('libertas-announcement-bar')) {
  class LibertasAnnouncementBar extends HTMLElement {
    connectedCallback() {
      if (this.dataset.announcementReady === 'true') return;
      this.dataset.announcementReady = 'true';
      this.viewport = this.querySelector('[data-announcement-viewport]');
      this.abortController = new AbortController();
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = 0;
      this.addEventListener('click', (event) => this.openKlaviyoForm(event), {
        signal: this.abortController.signal
      });

      if (this.dataset.mode === 'marquee') {
        this.setupMarquee();
      } else {
        this.setupCarousel();
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      window.clearInterval(this.autoplayTimer);
      window.cancelAnimationFrame(this.resizeFrame);
      delete this.dataset.announcementReady;
    }

    openKlaviyoForm(event) {
      const trigger = event.target.closest('[data-klaviyo-form-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      const formId = trigger.dataset.klaviyoFormId?.trim();
      if (!formId) return;

      event.preventDefault();
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', formId]);
      this.loadKlaviyoOnsite();
    }

    loadKlaviyoOnsite() {
      const existingScript = document.querySelector('script[src*="static.klaviyo.com/onsite/js/"]');
      if (existingScript) return;

      const publicApiKey = this.dataset.klaviyoPublicApiKey?.trim();
      if (!publicApiKey) return;

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://static.klaviyo.com/onsite/js/${encodeURIComponent(publicApiKey)}/klaviyo.js`;
      script.dataset.klaviyoOnsite = '';
      script.addEventListener('error', () => script.remove(), { once: true });
      document.head.appendChild(script);
    }

    setupMarquee() {
      this.track = this.querySelector('[data-announcement-marquee-track]');
      this.cycle = this.querySelector('[data-announcement-marquee-cycle]');
      if (!this.viewport || !this.track || !this.cycle) return;

      const rebuild = () => {
        this.track.classList.remove('is-ready');
        this.track.querySelectorAll('[data-announcement-generated-cycle]').forEach((node) => node.remove());
        this.cycle.querySelectorAll('[data-announcement-generated]').forEach((node) => node.remove());

        if (this.motionQuery.matches) return;

        const originals = [...this.cycle.children];
        if (!originals.length) return;

        const originalWidth = this.cycle.getBoundingClientRect().width;
        if (originalWidth <= 0) return;

        const targetWidth = this.viewport.clientWidth * 1.15;
        const repetitions = Math.min(24, Math.max(0, Math.ceil(targetWidth / originalWidth) - 1));
        const fragment = document.createDocumentFragment();

        for (let repetition = 0; repetition < repetitions; repetition += 1) {
          originals.forEach((node) => {
            const clone = this.prepareClone(node);
            clone.dataset.announcementGenerated = '';
            fragment.appendChild(clone);
          });
        }

        this.cycle.appendChild(fragment);

        const duplicateCycle = this.prepareClone(this.cycle);
        duplicateCycle.removeAttribute('data-announcement-marquee-cycle');
        duplicateCycle.dataset.announcementGeneratedCycle = '';
        this.track.appendChild(duplicateCycle);

        const distance = this.cycle.getBoundingClientRect().width;
        const speed = Math.max(10, Number(this.dataset.marqueeSpeed) || 40);
        this.track.style.setProperty('--announcement-marquee-distance', `${distance}px`);
        this.track.style.setProperty('--announcement-marquee-duration', `${distance / speed}s`);
        window.requestAnimationFrame(() => this.track.classList.add('is-ready'));
      };

      this.resizeObserver = new ResizeObserver(() => {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = window.requestAnimationFrame(rebuild);
      });
      this.resizeObserver.observe(this.viewport);
      rebuild();
    }

    prepareClone(node) {
      const clone = node.cloneNode(true);
      const clonedNodes = [clone, ...clone.querySelectorAll('*')];
      clonedNodes.forEach((element) => {
        element.removeAttribute('id');
        element.removeAttribute('data-shopify-editor-block');
      });
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    setupCarousel() {
      this.slides = [...this.querySelectorAll('[data-announcement-slide]')];
      this.previousButton = this.querySelector('[data-announcement-previous]');
      this.nextButton = this.querySelector('[data-announcement-next]');
      if (!this.viewport || this.slides.length < 2) return;

      const { signal } = this.abortController;
      const move = (direction) => {
        this.goTo(this.activeIndex + direction);
        this.restartAutoplay();
      };

      this.previousButton?.addEventListener('click', () => move(-1), { signal });
      this.nextButton?.addEventListener('click', () => move(1), { signal });
      this.viewport.addEventListener('scroll', () => {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = window.requestAnimationFrame(() => this.updateActiveSlide());
      }, { passive: true, signal });
      this.viewport.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });
      this.addEventListener('pointerenter', () => this.stopAutoplay(), { signal });
      this.addEventListener('pointerleave', () => this.startAutoplay(), { signal });
      this.addEventListener('focusin', () => this.stopAutoplay(), { signal });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.startAutoplay();
      }, { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-announcement-slide]');
        if (!slide) return;
        this.stopAutoplay();
        this.goTo(this.slides.indexOf(slide), false);
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      }, { signal });

      this.resizeObserver = new ResizeObserver(() => this.goTo(this.activeIndex, false));
      this.resizeObserver.observe(this.viewport);
      this.updateActiveSlide();
      this.startAutoplay();
    }

    goTo(index, smooth = true) {
      if (!this.viewport || !this.slides?.length) return;
      this.activeIndex = (index + this.slides.length) % this.slides.length;
      this.viewport.scrollTo({
        left: this.activeIndex * this.viewport.clientWidth,
        behavior: smooth && !this.motionQuery.matches ? 'smooth' : 'auto'
      });
      this.updateSlideState();
    }

    updateActiveSlide() {
      if (!this.viewport?.clientWidth) return;
      this.activeIndex = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
      this.updateSlideState();
    }

    updateSlideState() {
      this.slides?.forEach((slide, index) => {
        if (index === this.activeIndex) slide.setAttribute('aria-current', 'true');
        else slide.removeAttribute('aria-current');
      });
    }

    startAutoplay() {
      if (
        this.dataset.autoplay !== 'true'
        || this.motionQuery.matches
        || document.hidden
        || this.slides?.length < 2
      ) return;

      this.stopAutoplay();
      const interval = Math.max(3, Number(this.dataset.autoplayInterval) || 5) * 1000;
      this.autoplayTimer = window.setInterval(() => this.goTo(this.activeIndex + 1), interval);
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define('libertas-announcement-bar', LibertasAnnouncementBar);
}
